/**
 * Top-level error boundary.
 *
 * Replaces `Sentry.ErrorBoundary` so that `@sentry/react` is not statically
 * imported from `main.tsx` (see `monitoring.ts` for why). Behaviour is the same
 * from the user's point of view: render the fallback, allow a reset, and report
 * the error — via `reportError`, which queues until the deferred SDK is ready.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react'
import { ErrorFallback } from '@/components/ErrorFallback'
import { reportError } from './monitoring'

interface Props {
  children: ReactNode
}

interface State {
  error: Error | null
  componentStack: string
}

export class AppErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, componentStack: '' }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error }
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    this.setState({ componentStack: info.componentStack ?? '' })
    reportError(error, { componentStack: info.componentStack ?? '' })
  }

  private readonly resetError = (): void => {
    this.setState({ error: null, componentStack: '' })
  }

  override render(): ReactNode {
    const { error, componentStack } = this.state
    if (error) {
      return (
        <ErrorFallback
          error={error}
          componentStack={componentStack}
          eventId=""
          resetError={this.resetError}
        />
      )
    }
    return this.props.children
  }
}
